import qs from "qs";

import { UrlProvider } from "../../../../api/UrlProvider";
import { getSectionSlugBySectionId } from "../../../../lib/getSectionSlugBySectionId";
import getStockPageSectionFromNotificationType, {
  NOTIFICATION_TYPES
} from "../../../../lib/getStockPageSectionFromNotificationType";
import {
  NOTIFICATION_NAVIGATION_KIND,
  resolveNotificationNavigation
} from "../../../../lib/notifications/resolveNotificationNavigation";
import { STRATEGY_DASHBOARD_CHART_MARKET_QUERY_PARAM } from "../../../../lib/StrategyDashboard/strategyDashboardChartMarkets";
import { showModal } from "../../../../lib/react-redux-modal-provider";

const getUrl = ({ urlPath, urlParams }) => {
  return { url: UrlProvider.getUrl(urlPath, urlParams).toLowerCase() };
};

const getNotificationLocalizedUrl = ({ urlPath, urlParams, locale }) => ({
  url: UrlProvider.getLocalizedUrl(locale, urlPath, {
    ...urlParams,
    locale
  }).toLowerCase()
});

const resolveSectionSlugForUrl = (sectionId, preparedSlugList) => {
  if (!sectionId) {
    return null;
  }

  return getSectionSlugBySectionId(preparedSlugList, sectionId);
};

const applyResolvedNotificationNavigation = ({
  intent,
  notificationType,
  locale,
  preparedSlugList
}) => {
  if (!intent) {
    return null;
  }

  if (intent.kind === NOTIFICATION_NAVIGATION_KIND.SID_DASHBOARD) {
    return getNotificationLocalizedUrl({
      locale,
      urlPath: "fe.sidDashboard",
      urlParams: { locale }
    });
  }

  if (intent.kind === NOTIFICATION_NAVIGATION_KIND.STOCK_SECTION) {
    const section = resolveSectionSlugForUrl(
      getStockPageSectionFromNotificationType(notificationType),
      preparedSlugList
    );
    const urlParams = {
      locale,
      marketId: intent.market,
      stockId: intent.ticker
    };

    if (section) {
      urlParams.section = section;
      return getUrl({
        urlPath: "fe.stockPageSection",
        urlParams
      });
    }

    return getUrl({
      urlPath: "fe.stockPage",
      urlParams
    });
  }

  if (intent.kind === NOTIFICATION_NAVIGATION_KIND.TREND_SUMMARY) {
    const base = getNotificationLocalizedUrl({
      locale,
      urlPath: "fe.turtleStrategySummary",
      urlParams: { locale }
    });
    const queryString = intent.query
      ? `?${qs.stringify(intent.query)}`
      : "";
    return { url: `${base.url}${queryString}` };
  }

  if (intent.kind === NOTIFICATION_NAVIGATION_KIND.TREND_DASHBOARD_SIGNALS) {
    const base = getNotificationLocalizedUrl({
      locale,
      urlPath: "fe.trendDashboardSignalsTab",
      urlParams: { locale }
    });
    const queryString = intent.market
      ? `?${qs.stringify({
          [STRATEGY_DASHBOARD_CHART_MARKET_QUERY_PARAM]: intent.market
        })}`
      : "";
    return { url: `${base.url}${queryString}` };
  }

  return null;
};

export const notificationItemFactory = ({
  item,
  analysisCategories,
  insiderAnalysisCategories,
  manualStrategies,
  locale,
  preparedSlugList
}): { url?: string, action?: Promise<void>, redirectTo?: string } => {
  const notificationType = item.notification_type;

  switch (notificationType) {
    case NOTIFICATION_TYPES.TRANSACTION_STRATEGY_HIGHS_AND_LOWS:
    case NOTIFICATION_TYPES.TRANSACTION_STRATEGY_SUPPLY_DEMAND:
    case NOTIFICATION_TYPES.TRANSACTION_STRATEGY_TREND: {
      const intent = resolveNotificationNavigation({
        notificationType,
        config: item.config
      });
      const resolvedNavigation = applyResolvedNotificationNavigation({
        intent,
        notificationType,
        locale,
        preparedSlugList
      });

      if (resolvedNavigation) {
        return resolvedNavigation;
      }

      console.warn(`Unhandled ${notificationType}`);
      return;
    }
    case NOTIFICATION_TYPES.DONCHAIN_H4: {
      const intent = resolveNotificationNavigation({
        notificationType,
        config: item.config
      });
      return applyResolvedNotificationNavigation({
        intent,
        notificationType,
        locale,
        preparedSlugList
      });
    }
    case NOTIFICATION_TYPES.SQUABER_TEAM_COMMENT: {
      return getNotificationLocalizedUrl({
        locale,
        urlPath: "fe.singleAnalysisPage",
        urlParams: {
          locale,
          id: item.config.related_object_id
        }
      });
    }
    case NOTIFICATION_TYPES.DEMAND_ZONE: {
      return getUrl({
        urlPath: "fe.stockPage",
        urlParams: {
          locale,
          marketId: item.config.market,
          stockId: item.config.ticker
        }
      });
    }
    case NOTIFICATION_TYPES.HIGHS_AND_LOWS_SUMMARY: {
      return getNotificationLocalizedUrl({
        urlPath: "fe.sidDashboard",
        locale
      });
    }
    case NOTIFICATION_TYPES.RECOMMENDATIONS:
    case NOTIFICATION_TYPES.PRICE_ALERTS:
    case NOTIFICATION_TYPES.HIGHS_AND_LOWS_CORRECTION:
    case NOTIFICATION_TYPES.MEDIA_MONITOR: {
      const notificationStockPageSection = resolveSectionSlugForUrl(
        item.notification_type
          ? getStockPageSectionFromNotificationType(item.notification_type)
          : null,
        preparedSlugList
      );

      const urlPath = notificationStockPageSection
        ? "fe.stockPageSection"
        : "fe.stockPage";

      const urlParams = {
        locale,
        marketId: item.config.market,
        stockId: item.config.ticker
      };

      if (notificationStockPageSection) {
        urlParams.section = notificationStockPageSection;
      }

      return getUrl({
        urlPath,
        urlParams
      });
    }
    default: {
      const redirectTo = item?.config?.redirect_to;

      if (redirectTo) {
        return { redirectTo };
      }

      const isHtmlMessage = item?.config?.html_message;

      if (isHtmlMessage) {
        return {
          action: () =>
            showModal("InterestingStockDescription", {
              title: item.shortcut,
              content: item.config.html_message
            })
        };
      }

      const isAnalysisCategoryNotification =
        analysisCategories?.findIndex?.(
          ({ notifications_unique_id }) =>
            notifications_unique_id === notificationType
        ) > -1;

      const isInsiderAnalysisCategoryNotification =
        insiderAnalysisCategories?.findIndex?.(
          ({ notifications_unique_id }) =>
            notifications_unique_id === notificationType
        ) > -1;

      if (isAnalysisCategoryNotification) {
        return getUrl({
          locale,
          urlPath: "fe.singleAnalysisPage",
          urlParams: {
            locale,
            id: item.config.related_object_id
          }
        });
      }

      if (isInsiderAnalysisCategoryNotification) {
        return getNotificationLocalizedUrl({
          locale,
          urlPath: "fe.singleInsiderAnalysisPage",
          urlParams: {
            locale,
            id: item.config.related_object_id
          }
        });
      }

      const isManualStrategiesNotification =
        manualStrategies?.data?.findIndex?.(
          ({ key }) => key === notificationType
        ) > -1;

      if (isManualStrategiesNotification) {
        return getUrl({
          urlPath: "fe.manualStrategyPage",
          urlParams: {
            locale,
            id: item.config.related_object_id
          }
        });
      }

      // Unhandled notifications
      console.warn(`Unhandled ${notificationType}`);
      return;
    }
  }
};
